import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// ─── Types ──────────────────────────────────────────────────────────────────

type AgentType = 'general' | 'pricing_strategy' | 'page_copy' | 'product_copy' | 'marketing' | 'media' | 'task_planner';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  agentType?: AgentType;
  pendingActions?: PendingAction[];
  incomplete?: boolean;
}

interface PendingAction {
  id: number;
  agentType: AgentType;
  status: 'pending' | 'applied' | 'dismissed';
  actionType: string;
  targetId?: string;
  description: string;
  payload: Record<string, any>;
  preview?: { before?: Record<string, any>; after: Record<string, any> };
}

interface ConversationSummary {
  conversationId: string;
  summary: string;
  updatedAt: string;
  createdAt: string;
}

interface UsageStats {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

// ─── Agent display config ───────────────────────────────────────────────────

const AGENT_BADGES: Record<AgentType, { label: string; color: string; bg: string }> = {
  general: { label: 'General Advisor', color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800' },
  pricing_strategy: { label: 'Pricing & Strategy', color: 'text-blue-700 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/50' },
  page_copy: { label: 'Page Copy', color: 'text-purple-700 dark:text-purple-300', bg: 'bg-purple-100 dark:bg-purple-900/50' },
  product_copy: { label: 'Product Copy', color: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-100 dark:bg-emerald-900/50' },
  marketing: { label: 'Marketing', color: 'text-amber-700 dark:text-amber-300', bg: 'bg-amber-100 dark:bg-amber-900/50' },
  media: { label: 'Media Intelligence', color: 'text-cyan-700 dark:text-cyan-300', bg: 'bg-cyan-100 dark:bg-cyan-900/50' },
  task_planner: { label: 'Task Planner', color: 'text-rose-700 dark:text-rose-300', bg: 'bg-rose-100 dark:bg-rose-900/50' },
};

const SUGGESTION_CHIPS = [
  { text: 'Analyze margins on all products', agent: 'pricing_strategy' as AgentType },
  { text: 'Which products are underpriced?', agent: 'pricing_strategy' as AgentType },
  { text: 'Rewrite the homepage hero copy', agent: 'page_copy' as AgentType },
  { text: 'Optimize SEO descriptions for earrings', agent: 'product_copy' as AgentType },
  { text: 'Create a Meta Ads campaign plan', agent: 'marketing' as AgentType },
  { text: 'What pricing changes would improve revenue?', agent: 'pricing_strategy' as AgentType },
  { text: 'Generate alt text for media missing it', agent: 'media' as AgentType },
  { text: 'Show me duplicate media', agent: 'media' as AgentType },
  { text: 'Plan my tasks for this week', agent: 'task_planner' as AgentType },
  { text: 'What should I focus on today?', agent: 'task_planner' as AgentType },
];

const AGENT_ICONS: Record<AgentType, string> = {
  general: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
  pricing_strategy: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  page_copy: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  product_copy: 'M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z',
  marketing: 'M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z',
  media: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  task_planner: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
};

// ─── Main Component ─────────────────────────────────────────────────────────

export function CMSAIAssistant() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Conversation state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [lastUsage, setLastUsage] = useState<UsageStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showTaskHistory, setShowTaskHistory] = useState(false);
  const [taskHistory, setTaskHistory] = useState<PendingAction[]>([]);

  // Load conversation list
  useEffect(() => {
    if (!token) return;
    loadConversations();
  }, [token]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function loadConversations() {
    setLoadingConversations(true);
    try {
      const res = await fetch('/api/cms/ai?action=list', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setConversations(data.conversations || []);
      }
    } catch {
      // Silently fail — list is non-critical
    } finally {
      setLoadingConversations(false);
    }
  }

  async function loadConversation(conversationId: string) {
    setActiveConversationId(conversationId);
    setMessages([]);
    setLastUsage(null);
    setError(null);
    setLoadingMessages(true);

    try {
      const res = await fetch(`/api/cms/ai?action=get&conversationId=${encodeURIComponent(conversationId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        const loadedMessages: Message[] = (data.messages || []).map((m: any) => ({
          role: m.role,
          content: m.content,
          timestamp: m.timestamp,
          agentType: m.agentType || undefined,
        }));

        // Attach pending actions to the last assistant message if available
        if (data.tasks && data.tasks.length > 0) {
          const pendingTasks = data.tasks.filter((t: any) => t.status === 'pending');
          if (pendingTasks.length > 0) {
            // Find the last assistant message and attach actions
            for (let i = loadedMessages.length - 1; i >= 0; i--) {
              if (loadedMessages[i].role === 'assistant') {
                loadedMessages[i].pendingActions = pendingTasks;
                break;
              }
            }
          }
        }

        setMessages(loadedMessages);
      }
    } catch {
      // Failed to load messages — user can still send new ones
    } finally {
      setLoadingMessages(false);
    }
  }

  async function sendMessage(text?: string) {
    const messageText = text || input.trim();
    if (!messageText || sending) return;

    setInput('');
    setError(null);
    setSending(true);
    setProgressMessage('Analyzing your request...');

    const userMessage: Message = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMessage]);

    try {
      const res = await fetch('/api/cms/ai?action=chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          conversationId: activeConversationId,
          message: messageText,
        }),
      });

      if (!res.ok) {
        // Non-streaming error (auth failures, bad requests)
        const data = await res.json().catch(() => ({ error: 'Failed to get response' }));
        throw new Error(data.error || 'Failed to get response');
      }

      // Read SSE stream
      const reader = res.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE events (separated by double newlines)
        const parts = buffer.split('\n\n');
        buffer = parts.pop()!; // Keep incomplete chunk in buffer

        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data: ')) continue;

          let data: any;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue; // Skip malformed JSON
          }

          if (data.type === 'status' || data.type === 'progress') {
            setProgressMessage(data.message);
          } else if (data.type === 'agent') {
            setProgressMessage(`Routed to ${AGENT_BADGES[data.agentType as AgentType]?.label || 'agent'}...`);
          } else if (data.type === 'done') {
            if (!activeConversationId && data.conversationId) {
              setActiveConversationId(data.conversationId);
            }
            const assistantMessage: Message = {
              role: 'assistant',
              content: data.response,
              timestamp: new Date().toISOString(),
              agentType: data.agentType,
              pendingActions: data.pendingActions?.length > 0 ? data.pendingActions : undefined,
              incomplete: data.incomplete || false,
            };
            setMessages((prev) => [...prev, assistantMessage]);
            setLastUsage(data.usage);
            loadConversations();
          } else if (data.type === 'error') {
            throw new Error(data.error || 'Agent error');
          }
          // heartbeat: ignore
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSending(false);
      setProgressMessage(null);
      inputRef.current?.focus();
    }
  }

  async function handleApplyAction(actionId: number, messageIndex: number) {
    try {
      const res = await fetch('/api/cms/ai?action=apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ actionId }),
      });

      const data = await res.json();

      setMessages((prev) =>
        prev.map((msg, idx) => {
          if (idx !== messageIndex || !msg.pendingActions) return msg;
          return {
            ...msg,
            pendingActions: msg.pendingActions.map((a) =>
              a.id === actionId
                ? { ...a, status: data.success ? 'applied' as const : 'pending' as const }
                : a
            ),
          };
        })
      );

      if (!data.success) {
        setError(data.error || 'Failed to apply action');
      }
    } catch {
      setError('Failed to apply action');
    }
  }

  async function handleDismissAction(actionId: number, messageIndex: number) {
    try {
      await fetch('/api/cms/ai?action=dismiss', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ actionId }),
      });

      setMessages((prev) =>
        prev.map((msg, idx) => {
          if (idx !== messageIndex || !msg.pendingActions) return msg;
          return {
            ...msg,
            pendingActions: msg.pendingActions.map((a) =>
              a.id === actionId ? { ...a, status: 'dismissed' as const } : a
            ),
          };
        })
      );
    } catch {
      setError('Failed to dismiss action');
    }
  }

  async function handleApplyAll(messageIndex: number) {
    const msg = messages[messageIndex];
    if (!msg?.pendingActions) return;
    const pending = msg.pendingActions.filter((a) => a.status === 'pending');
    for (const action of pending) {
      await handleApplyAction(action.id, messageIndex);
    }
  }

  async function loadTaskHistory() {
    try {
      const res = await fetch('/api/cms/ai?action=tasks&limit=50', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTaskHistory(data.tasks || []);
      }
    } catch { /* ignore */ }
    setShowTaskHistory(true);
  }

  function deleteConversation(conversationId: string) {
    fetch('/api/cms/ai?action=delete', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ conversationId }),
    }).then(() => {
      setConversations((prev) => prev.filter((c) => c.conversationId !== conversationId));
      if (activeConversationId === conversationId) {
        startNewConversation();
      }
    }).catch(() => { /* ignore */ });
  }

  function startNewConversation() {
    setActiveConversationId(null);
    setMessages([]);
    setLastUsage(null);
    setError(null);
    inputRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  function formatTime(iso: string) {
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now.getTime() - d.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return d.toLocaleDateString([], { weekday: 'short' });
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch {
      return '';
    }
  }

  return (
    <div className="h-dvh bg-brand-sand dark:bg-brand-dark flex flex-col">
      {/* Header */}
      <header className="bg-white/80 dark:bg-brand-charcoal/60 backdrop-blur-md border-b border-black/5 dark:border-white/10 flex-shrink-0">
        <div className="px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 -ml-1 text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => navigate('/cms/dashboard')}
              className="text-sm text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white transition-colors hidden md:block"
            >
              ← Dashboard
            </button>
            <div className="h-4 w-px bg-black/10 dark:bg-white/10 hidden md:block" />
            <h1 className="text-lg font-semibold text-brand-dark dark:text-white">AI Agents</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadTaskHistory}
              className="p-2 md:px-3 md:py-1.5 text-xs border border-black/10 dark:border-white/10 rounded-lg text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white hover:border-black/20 dark:hover:border-white/20 transition-colors"
            >
              <svg className="w-4 h-4 md:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="hidden md:inline">Task History</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden w-full">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Conversation List */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-50 w-72 transition-transform duration-200
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            md:relative md:z-auto md:inset-auto md:translate-x-0
            ${sidebarOpen ? 'md:w-64' : 'md:w-0'}
            flex-shrink-0 border-r border-black/5 dark:border-white/10
            bg-white dark:bg-brand-charcoal md:bg-white/60 md:dark:bg-brand-charcoal/40
            backdrop-blur-md overflow-hidden flex flex-col md:transition-all
          `}
        >
          {/* Mobile drawer header */}
          <div className="flex items-center justify-between p-3 border-b border-black/5 dark:border-white/10 md:hidden">
            <button
              onClick={() => { setSidebarOpen(false); navigate('/cms/dashboard'); }}
              className="text-sm text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white transition-colors"
            >
              ← Dashboard
            </button>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-1.5 text-brand-gray dark:text-white/60 hover:text-brand-dark dark:hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-3">
            <button
              onClick={() => { startNewConversation(); if (window.innerWidth < 768) setSidebarOpen(false); }}
              className="w-full px-3 py-2.5 border border-black/10 dark:border-white/10 hover:border-brand-blue dark:hover:border-brand-blue text-brand-dark dark:text-white rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-2 pb-2">
            {loadingConversations ? (
              <div className="p-4 text-center text-brand-gray dark:text-white/60 text-xs">
                Loading...
              </div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-brand-gray dark:text-white/40 text-xs">
                Start a conversation to see it here
              </div>
            ) : (
              <div className="space-y-0.5">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversationId}
                    className={`group flex items-center gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                      activeConversationId === conv.conversationId
                        ? 'bg-brand-blue/10 dark:bg-brand-blue/10 text-brand-dark dark:text-white'
                        : 'hover:bg-black/[0.03] dark:hover:bg-white/[0.04] text-brand-dark/80 dark:text-white/70'
                    }`}
                    onClick={() => { loadConversation(conv.conversationId); if (window.innerWidth < 768) setSidebarOpen(false); }}
                  >
                    <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate">
                        {conv.summary || 'Untitled'}
                      </p>
                      <p className="text-[10px] opacity-50 mt-0.5">
                        {formatTime(conv.updatedAt)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conv.conversationId);
                      }}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all rounded"
                      title="Delete"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Chat Area */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
            {loadingMessages ? (
              <div className="flex items-center justify-center h-full">
                <div className="flex items-center gap-2 text-brand-gray dark:text-white/50 text-sm">
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Loading...
                </div>
              </div>
            ) : messages.length === 0 && !sending ? (
              /* Empty State */
              <div className="flex flex-col items-center justify-center h-full px-4 py-8">
                <p className="text-sm text-brand-gray dark:text-white/40 mb-6">
                  Ask anything — your message is routed to the right specialist.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                  {SUGGESTION_CHIPS.map((chip) => {
                    const badge = AGENT_BADGES[chip.agent];
                    const iconPath = AGENT_ICONS[chip.agent];
                    return (
                      <button
                        key={chip.text}
                        onClick={() => sendMessage(chip.text)}
                        className="group flex items-start gap-3 px-3.5 py-3 text-left bg-white/60 dark:bg-white/[0.03] border border-black/[0.06] dark:border-white/[0.06] rounded-xl hover:border-brand-blue/40 dark:hover:border-brand-blue/30 hover:bg-white dark:hover:bg-white/[0.06] transition-all"
                      >
                        <div className={`mt-0.5 p-1.5 rounded-lg ${badge.bg} flex-shrink-0`}>
                          <svg className={`w-3.5 h-3.5 ${badge.color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm text-brand-dark/80 dark:text-white/80 group-hover:text-brand-dark dark:group-hover:text-white transition-colors">
                            {chip.text}
                          </p>
                          <p className={`text-[10px] mt-0.5 ${badge.color} opacity-70`}>
                            {badge.label}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              /* Message List */
              <>
                {messages.map((msg, idx) => (
                  <div key={idx}>
                    <div
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[88%] md:max-w-[75%] ${
                          msg.role === 'user'
                            ? 'bg-brand-blue text-white rounded-2xl rounded-br-md px-4 py-2.5'
                            : 'bg-white/80 dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.06] text-brand-dark dark:text-gray-100 rounded-2xl rounded-bl-md px-4 py-3'
                        }`}
                      >
                        {/* Agent Badge */}
                        {msg.role === 'assistant' && msg.agentType && (
                          <AgentBadge agentType={msg.agentType} />
                        )}

                        {msg.role === 'assistant' ? (
                          <div className="prose prose-sm dark:prose-invert max-w-none whitespace-pre-wrap [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                            <FormattedMessage content={msg.content} />
                          </div>
                        ) : (
                          <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                        )}
                      </div>
                    </div>

                    {/* Continue Button for incomplete responses */}
                    {msg.incomplete && msg.role === 'assistant' && idx === messages.length - 1 && !sending && (
                      <div className="flex justify-start mt-2">
                        <button
                          onClick={() => sendMessage('Continue your analysis from where you left off.')}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-brand-blue/30 dark:border-brand-blue/20 text-brand-blue hover:bg-brand-blue/10 dark:hover:bg-brand-blue/10 rounded-xl transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Continue
                        </button>
                      </div>
                    )}

                    {/* Pending Action Cards */}
                    {msg.pendingActions && msg.pendingActions.length > 0 && (
                      <div className="flex justify-start mt-2">
                        <div className="max-w-[92%] md:max-w-[80%] space-y-2">
                          {msg.pendingActions.filter((a) => a.status === 'pending').length > 1 && (
                            <button
                              onClick={() => handleApplyAll(idx)}
                              className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                            >
                              Apply All ({msg.pendingActions.filter((a) => a.status === 'pending').length})
                            </button>
                          )}
                          {msg.pendingActions.map((action) => (
                            <ActionCard
                              key={action.id}
                              action={action}
                              onApply={() => handleApplyAction(action.id, idx)}
                              onDismiss={() => handleDismissAction(action.id, idx)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {/* Typing / Progress Indicator */}
                {sending && (
                  <div className="flex justify-start">
                    <div className="bg-white/80 dark:bg-white/[0.04] border border-black/[0.06] dark:border-white/[0.06] rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-brand-blue/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-brand-blue/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-brand-blue/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        {progressMessage && (
                          <span className="text-xs text-brand-gray dark:text-white/50 animate-pulse">
                            {progressMessage}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="mx-4 md:mx-6 mb-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {/* Input Area */}
          <div className="p-3 border-t border-black/5 dark:border-white/10 bg-white/60 dark:bg-brand-charcoal/40 backdrop-blur-md">
            {/* Usage Stats — subtle inline */}
            {lastUsage && (
              <div className="flex gap-3 mb-2 px-1 text-[10px] text-black/25 dark:text-white/20">
                <span>{lastUsage.input_tokens + lastUsage.output_tokens} tokens</span>
                {lastUsage.cache_read_input_tokens > 0 && (
                  <span>{lastUsage.cache_read_input_tokens} cached</span>
                )}
              </div>
            )}
            <div className="flex gap-2 items-end max-w-3xl mx-auto">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask about pricing, copy, SEO, or marketing..."
                  className="w-full resize-none rounded-xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-white/[0.04] px-4 py-3 pr-14 text-sm md:text-sm text-brand-dark dark:text-white placeholder-black/30 dark:placeholder-white/25 focus:outline-none focus:border-brand-blue/50 dark:focus:border-brand-blue/30 transition-colors"
                  rows={1}
                  disabled={sending}
                  onInput={(e) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = 'auto';
                    target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                  }}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={!input.trim() || sending}
                  className="absolute right-2 bottom-1.5 p-2.5 text-brand-blue disabled:text-black/15 dark:disabled:text-white/15 hover:text-brand-dark dark:hover:text-white transition-colors rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Task History Modal */}
      {showTaskHistory && (
        <TaskHistoryModal
          tasks={taskHistory}
          onClose={() => setShowTaskHistory(false)}
        />
      )}
    </div>
  );
}

// ─── Agent Badge ────────────────────────────────────────────────────────────

function AgentBadge({ agentType }: { agentType: AgentType }) {
  const badge = AGENT_BADGES[agentType] || AGENT_BADGES.general;
  const iconPath = AGENT_ICONS[agentType] || AGENT_ICONS.general;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full mb-2 ${badge.bg} ${badge.color}`}>
      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
      </svg>
      {badge.label}
    </span>
  );
}

// ─── Action Card ────────────────────────────────────────────────────────────

function ActionCard({
  action,
  onApply,
  onDismiss,
}: {
  action: PendingAction;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const isResolved = action.status !== 'pending';

  return (
    <div className={`rounded-xl border overflow-hidden text-sm ${
      action.status === 'applied'
        ? 'border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-950/30'
        : action.status === 'dismissed'
        ? 'border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 opacity-60'
        : 'border-brand-blue/30 dark:border-brand-blue/20 bg-white dark:bg-brand-charcoal/60'
    }`}>
      {/* Header */}
      <div className="px-3 py-2 border-b border-black/5 dark:border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${
            action.status === 'applied' ? 'bg-green-500' :
            action.status === 'dismissed' ? 'bg-gray-400' : 'bg-amber-500'
          }`} />
          <span className="font-medium text-brand-dark dark:text-white">
            {action.actionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
          </span>
          {action.targetId && (
            <span className="text-xs text-brand-gray dark:text-white/50 font-mono">
              {action.targetId.length > 30 ? action.targetId.slice(0, 30) + '...' : action.targetId}
            </span>
          )}
        </div>
        {isResolved && (
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            action.status === 'applied'
              ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
          }`}>
            {action.status === 'applied' ? 'Applied' : 'Dismissed'}
          </span>
        )}
      </div>

      {/* Description */}
      <div className="px-3 py-2 text-brand-dark/80 dark:text-white/80">
        {action.description}
      </div>

      {/* Preview */}
      {action.preview && (
        <div className="px-3 py-2 border-t border-black/5 dark:border-white/10 font-mono text-xs">
          {action.preview.before && Object.entries(action.preview.before).map(([key, val]) => (
            <div key={`before-${key}`} className="flex">
              <span className="text-red-500 mr-1 select-none w-3">-</span>
              <span className="text-brand-gray dark:text-white/50 mr-1">{key}:</span>
              <span className="text-red-600 dark:text-red-400 break-all">
                {typeof val === 'string' ? (val.length > 120 ? val.slice(0, 120) + '...' : val) : JSON.stringify(val)}
              </span>
            </div>
          ))}
          {action.preview.after && Object.entries(action.preview.after).map(([key, val]) => (
            <div key={`after-${key}`} className="flex">
              <span className="text-green-500 mr-1 select-none w-3">+</span>
              <span className="text-brand-gray dark:text-white/50 mr-1">{key}:</span>
              <span className="text-green-600 dark:text-green-400 break-all">
                {typeof val === 'string' ? (val.length > 120 ? val.slice(0, 120) + '...' : val) : JSON.stringify(val)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Action Buttons */}
      {!isResolved && (
        <div className="px-3 py-2 border-t border-black/5 dark:border-white/10 flex gap-2">
          <button
            onClick={onApply}
            className="px-3 py-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            Apply
          </button>
          <button
            onClick={onDismiss}
            className="px-3 py-1.5 text-xs font-medium border border-black/15 dark:border-white/15 text-brand-gray dark:text-white/60 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Task History Modal ─────────────────────────────────────────────────────

function TaskHistoryModal({
  tasks,
  onClose,
}: {
  tasks: PendingAction[];
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-brand-charcoal rounded-2xl shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-black/10 dark:border-white/10">
          <h2 className="text-lg font-bold text-brand-dark dark:text-white">Task History</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-brand-dark dark:hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Task List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {tasks.length === 0 ? (
            <p className="text-center text-brand-gray dark:text-white/60 py-8">
              No agent actions yet
            </p>
          ) : (
            tasks.map((task) => {
              const badge = AGENT_BADGES[task.agentType] || AGENT_BADGES.general;
              return (
                <div
                  key={task.id}
                  className="rounded-lg border border-black/10 dark:border-white/10 p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${
                      task.status === 'applied' ? 'bg-green-500' :
                      task.status === 'dismissed' ? 'bg-gray-400' : 'bg-amber-500'
                    }`} />
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${badge.bg} ${badge.color}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-brand-gray dark:text-white/50">
                      {task.actionType.replace(/_/g, ' ')}
                    </span>
                    <span className={`ml-auto text-xs font-medium ${
                      task.status === 'applied' ? 'text-green-600 dark:text-green-400' :
                      task.status === 'dismissed' ? 'text-gray-500' : 'text-amber-600 dark:text-amber-400'
                    }`}>
                      {task.status}
                    </span>
                  </div>
                  <p className="text-sm text-brand-dark/80 dark:text-white/80">{task.description}</p>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Formatters ─────────────────────────────────────────────────────────────

/** Simple markdown-like formatter for assistant messages */
function FormattedMessage({ content }: { content: string }) {
  const lines = content.split('\n');

  return (
    <>
      {lines.map((line, i) => {
        // Headers
        if (line.startsWith('### ')) {
          return <h4 key={i} className="font-semibold text-base mt-3 mb-1">{line.slice(4)}</h4>;
        }
        if (line.startsWith('## ')) {
          return <h3 key={i} className="font-semibold text-lg mt-4 mb-1">{line.slice(3)}</h3>;
        }
        if (line.startsWith('# ')) {
          return <h2 key={i} className="font-bold text-xl mt-4 mb-2">{line.slice(2)}</h2>;
        }

        // Bullet points
        if (line.match(/^[-*] /)) {
          return (
            <div key={i} className="flex gap-2 ml-2 my-0.5">
              <span className="text-brand-blue flex-shrink-0">•</span>
              <span><InlineFormatted text={line.slice(2)} /></span>
            </div>
          );
        }

        // Numbered lists
        const numberedMatch = line.match(/^(\d+)\.\s/);
        if (numberedMatch) {
          return (
            <div key={i} className="flex gap-2 ml-2 my-0.5">
              <span className="text-brand-blue font-medium flex-shrink-0">{numberedMatch[1]}.</span>
              <span><InlineFormatted text={line.slice(numberedMatch[0].length)} /></span>
            </div>
          );
        }

        // Empty lines
        if (!line.trim()) {
          return <div key={i} className="h-2" />;
        }

        // Regular text
        return <p key={i} className="my-0.5"><InlineFormatted text={line} /></p>;
      })}
    </>
  );
}

/** Handle inline bold/italic */
function InlineFormatted({ text }: { text: string }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);

  return (
    <>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={i}>{part.slice(1, -1)}</em>;
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
